import * as React from "react";
import { ClassAttributes, Component } from "react";
import ObservationCard from "../observation/ObservationCard";
import EventBus, { Events } from "../../service/EventBus";
import Autocomplete from "../autocomplete/Autocomplete";
import ObsBadge from "../observation/ObsBadge";
import { Negative, Observation, Study } from "../../api/StudyApi";
import { ObservationType } from "../../api/GrammarApi";
import { LetterBadge } from "../../service/LetterBadgeManager";
import { AuthManager } from "../auth/AuthContext";
import LocalStorageService from "../../service/LocalStorageService";
import i18n from "i18next";

interface ObservationsProps extends ClassAttributes<Observations> {
  study: Study;

  selectedIdx: number;

  onSelect(idx: number): void;

  getLetterBadge(obsIdx: number): LetterBadge;

  createObservation(type: string): Promise<Observation>;

  createNegative(type: string): Promise<Negative>;

  removeObservation(obs: Observation): void;

  removeNegative(neg: Negative): void;

  authManager: AuthManager;
}

interface ObservationsState {
  observationTypes: Array<ObservationType>;
}

export default class Observations extends Component<ObservationsProps, ObservationsState> {
  constructor(props: ObservationsProps) {
    super(props);

    this.state = { observationTypes: [] };
  }

  componentDidMount() {
    this.setState({ observationTypes: LocalStorageService.getObservationTypes() });
  }

  removeObservation(observation: Observation) {
    this.props.removeObservation(observation);
  }

  showRegionForObs(obs: Observation) {
    EventBus.publish(Events.obs_showregion, obs);
  }

  createNewObs(obsType: string): Promise<Observation> {
    return this.props.createObservation(obsType).then((obs) => {
      const idx = this.props.study.observations.indexOf(obs);
      this.props.onSelect(idx);
      return obs;
    });
  }

  private createNewNegative(obsType: string) {
    this.props.createNegative(obsType).then((neg) => {
      this.props.onSelect(this.translateIdxForNegative(this.props.study.negatives.indexOf(neg)));
    });
  }

  private translateIdxForNegative(negIdx: number) {
    return this.props.study.observations.length + negIdx;
  }

  render() {
    const negativeClass = (idx: number): string => {
      let cl = "observation cursor-pointer";
      if (this.translateIdxForNegative(idx) === this.props.selectedIdx) {
        cl += " selected";
      }
      return cl;
    };

    return (
      <div>
        <div className="card">
          <div className="card-title">{i18n.t("obs.posObs")}</div>

          <div className="card-body">
            <Autocomplete
              onChange={(obsType) => this.createNewObs(obsType.code)}
              placeholder={i18n.t("obs.addObs")}
              items={this.state.observationTypes}
              toLabel={(ot) => ot.label}
              renderItem={(ot) => (
                <span>
                  <ObsBadge key={ot.code} color={ot.color} text={ot.code} />
                  {ot.label}
                </span>
              )}
            />

            {this.props.study.observations &&
              this.props.study.observations.map((obs, idx) => (
                <ObservationCard
                  key={obs.id}
                  obs={obs}
                  studyId={this.props.study.id}
                  selected={idx == this.props.selectedIdx}
                  removeObservation={(obs) => this.removeObservation(obs)}
                  onClickObservation={() => this.props.onSelect(idx)}
                  onClickTargetObs={(obs) => this.showRegionForObs(obs)}
                  authManager={this.props.authManager}
                />
              ))}
          </div>
        </div>

        <div className="card">
          <div className="card-title">{i18n.t("obs.negObs")}</div>

          <div className="card-body">
            <Autocomplete
              onChange={(obsType) => this.createNewNegative(obsType.code)}
              placeholder={i18n.t("obs.addNeg")}
              items={this.state.observationTypes}
              toLabel={(ot) => ot.label}
              renderItem={(ot) => (
                <span>
                  <ObsBadge key={ot.code} color={ot.color} text={ot.code} />
                  {ot.label}
                </span>
              )}
            />

            {this.props.study.negatives &&
              this.props.study.negatives.map((neg, idx) => (
                <div
                  key={idx}
                  className={negativeClass(idx)}
                  onClick={() => this.props.onSelect(this.translateIdxForNegative(idx))}
                >
                  <div
                    className="observation-title"
                    style={{ color: LocalStorageService.getObservationType(neg.type).negativeColor }}
                  >
                    NO {neg.type.toUpperCase()}
                    <button className="btn btn-secondary pull-right" onClick={(evt) => this.props.removeNegative(neg)}>
                      <i className="fa fa-trash" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  }
}
